import { FileSpreadsheet, Printer, ChevronLeft, Search, CheckSquare, Square, Loader2, Award, RefreshCcw, Trash2, Eye, Monitor, FileDown, BookX } from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PracticeExam } from '../../types/practice-exam';
import { ExamResultList } from './components/ExamResultList';
import WebStudentReportCard, { alanScopedSuccess, yksScopedSuccess } from './components/WebStudentReportCard';
import ReportCardPdfPreview from './components/ReportCardPdfPreview';
import { useAuth } from '../../contexts/AuthContext';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';

function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

// Sunucu tek istekte en çok bu kadar karne basar (BE MAX_BULK_REPORT_CARDS ile aynı); A4 önizleme yalnız ilk birkaç karne
const KARNE_PARCA = 50;
// Satırın okulu: sunucunun eklediği row.school (okul etiketi ya da okulun kayıtlı öğrenci bölümü), yoksa satırdaki okul etiketi
const schoolOf = (row: any): { id: string; name: string } | null => row?.school
    || (row?.student?.external?.schoolId ? { id: row.student.external.schoolId, name: row.student.external.schoolName || 'Dış okul' } : null);
const ONIZLEME_TAVANI = 5;

// Detaylı karne analizi PDF'i sunucuda üretilir; gelen dosyayı tarayıcıya indir (Karnem ve öğrenci raporu da kullanır).
export const saveReportCardPdf = ({ blob, fileName }: { blob: Blob; fileName?: string }) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'karne.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Hemen iptal edilirse Safari (Mac/iPad) indirmeyi sessizce düşürüyor (27 Eki, TED): dosya alınana kadar beklenir
    setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
};

// Karneyi yazdırma penceresinde aç: "Yazdır" indirilen detaylı karnenin aynısını basar (tek şablon). Gizli çerçevede
// tarayıcının PDF görüntüleyicisi yazdırır; Safari gibi çerçeveden yazdırmayanlarda PDF yeni sekmede açılır.
export const printPdfBlob = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome|crios|chromium|edg/i.test(navigator.userAgent);
    if (isSafari) {
        if (!window.open(url, '_blank')) saveReportCardPdf({ blob, fileName: 'karneler.pdf' });
        return;
    }
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    frame.src = url;
    frame.onload = () => {
        try {
            frame.contentWindow?.focus();
            frame.contentWindow?.print();
        } catch {
            window.open(url, '_blank');
        }
    };
    document.body.appendChild(frame);
    // Yazdırma penceresi açıkken çerçeve kaldırılırsa baskı iptal olur; geç temizlenir.
    setTimeout(() => { frame.remove(); window.URL.revokeObjectURL(url); }, 10 * 60 * 1000);
};

export default function ReportCards() {
    const { showAlert, showConfirm } = useModal();
    const { systemConfig } = useAuth();
    const [loading, setLoading] = useState(false);
    const [exams, setExams] = useState<PracticeExam[]>([]);
    const [examsLoaded, setExamsLoaded] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    // Deneme kartlarında arama ve tür (deneme sayısı arttıkça kartlar arasında kaybolunmasın)
    const [examSearch, setExamSearch] = useState('');
    const [examType, setExamType] = useState<'' | 'yks' | 'lgs'>('');
    const [selectedExam, setSelectedExam] = useState<PracticeExam | null>(null);

    const [resultsLoading, setResultsLoading] = useState(false);
    const [bulkActionLoading, setBulkActionLoading] = useState<'delete' | 'regenerate' | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    // Toplu karne parça parça indirilir (sunucu tek istekte en çok KARNE_PARCA basar; 26 Eki: 350 karne tek seferde ≈3,4 GB bellek)
    const [pdfProgress, setPdfProgress] = useState<{ done: number; total: number } | null>(null);
    // Parçalı indirmenin dosyaları: tarayıcı art arda otomatik indirmeyi engelleyebilir; her parça tıklanarak yeniden indirilir
    const [pdfParts, setPdfParts] = useState<{ blob: Blob; fileName: string; label: string }[]>([]);
    const [printLoading, setPrintLoading] = useState(false);
    const [bookletLoading, setBookletLoading] = useState(false);
    const [resultsData, setResultsData] = useState<any[]>([]);
    const [examMetadata, setExamMetadata] = useState<{ bookletMapping?: any; testNames?: any; testAverages?: any; overallStats?: any; levelType?: PracticeExam['levelType']; yksMode?: PracticeExam['yksMode']; systemType?: PracticeExam['systemType'] }>({});

    const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearch = useDebounce(searchQuery, 300);

    const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
    const [previewMode, setPreviewMode] = useState<'system' | 'a4'>('system');
    // A4 önizleme PDF'i sonuçlar yeniden yüklenince (Tekrar Oluştur, yapay zekâ analizi) yeniden istenir
    const [resultsVersion, setResultsVersion] = useState(0);
    // A4 önizleme telefon ya da masaüstü alanından yalnız birinde çizilir: PDF iki kez üretilmesin
    const [isDesktopViewport, setIsDesktopViewport] = useState(() => typeof window === 'undefined' || !window.matchMedia || window.matchMedia('(min-width: 768px)').matches);

    const printRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchExams();
    }, []);

    // Başka denemeye geçince önceki denemenin parça dosyaları gösterilmez
    useEffect(() => { setPdfParts([]); }, [selectedExam?.id]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia('(min-width: 768px)');
        const compute = () => setIsDesktopViewport(mql.matches);
        compute();
        mql.addEventListener('change', compute);
        return () => mql.removeEventListener('change', compute);
    }, []);

    const fetchExams = async () => {
        try {
            setLoading(true);

            // Yalnız sonucu yüklenmiş denemeler; sınav tarihine göre en yeni üstte (eskiden ilk 100 deneme içinden süzülüyordu)
            const res = await ApiService.getPracticeExams({ limit: 1000, status: 'with-results' });

            const allExams = Array.isArray((res as any)?.rows) ? (res as any).rows : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

            const examsWithResults = allExams.filter((e: any) => e.stats === true);
            setExams(examsWithResults);
        } catch (error) {
            console.error(error);
            showAlert({ title: 'Hata', message: 'Denemeler yüklenirken hata oluştu', type: 'error' });
        } finally {
            setLoading(false);
            setExamsLoaded(true);
        }
    };

    const loadExamResults = async (exam: PracticeExam) => {
        const res = await ApiService.getPracticeExamResults(exam.id, { limit: 10000 });
        const rows = Array.isArray((res as any)?.rows) ? (res as any).rows : [];
        setResultsData(rows);
        setResultsVersion((value) => value + 1);
        setExamMetadata({
            bookletMapping: (res as any)?.bookletMapping || {},
            testNames: (res as any)?.testNames || {},
            testAverages: (res as any)?.testAverages || {},
            overallStats: (res as any)?.overallStats || {},
            levelType: exam.levelType,
            yksMode: exam.yksMode,
            systemType: exam.systemType || (exam.isInternal ? 'internal' : undefined),
        });
    };

    const foldText = (value: string) => (value || '').toLocaleLowerCase('tr-TR')
        .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c');
    const visibleExams = exams.filter((exam) => (!examType || exam.levelType === examType)
        && (!examSearch.trim() || foldText(exam.name || '').includes(foldText(examSearch.trim()))));

    // Akademi sistemi YKS: listedeki yüzde karnedeki özetle aynı (TYT + alan testleri); yayınevi alan puanlı denemede alanın
    // testleri; diğer denemelerde kayıtlı başarı yüzdesi
    const isInternalYksExam = selectedExam?.levelType === 'yks' && (selectedExam?.systemType === 'internal' || Boolean(selectedExam?.isInternal));
    const listSuccess = (student: any) => (student?.results?.alanPuani?.testIds?.length
        ? alanScopedSuccess(student.answers || {}, student.results.alanPuani.testIds.map(String))
        : isInternalYksExam
        ? yksScopedSuccess(
            student.answers || {},
            student.stats?.scoring?.primaryScore?.scoreType,
            selectedExam?.yksMode === 'single_ayt' ? student.stats?.scoring?.primaryScore?.scoreType : student.student?.metadata?.alan,
        )
        : Number(student.stats?.scorePercentage || 0));

    // "Tüm Denemeler"deki Sonuçlar düğmesi buraya ?exam=<id> ile gelir: denemeler yüklenince o deneme açılır, değer adresten silinir
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const examId = params.get('exam');
        if (!examId || !examsLoaded) return;
        params.delete('exam');
        navigate(`?${params.toString()}`, { replace: true });
        const target = exams.find((exam) => exam.id === examId);
        if (target) handleExamClick(target);
        else showAlert({ title: 'Bilgi', message: 'Bu denemenin sonuçları henüz yüklenmemiş.', type: 'info' });
    }, [location.search, examsLoaded]);

    // Kaydedilen analiz listedeki satıra da işlenir: başka öğrenciye geçip dönünce yeniden sorulmasın
    const handleAnalysisSaved = (resultId: string, aiAnalysis: { content: any; generatedAt: string }) => {
        setResultsData((rows) => rows.map((row) => (row.id === resultId ? { ...row, stats: { ...(row.stats || {}), aiAnalysis } } : row)));
        setResultsVersion((value) => value + 1);
    };

    // Seçim liste sırasıyla (parçalar ve önizleme ekrandaki sırayı izlesin)
    const orderedSelectedIds = () => resultsData.map((row: any) => row.id).filter((id: string) => selectedStudentIds.has(id));
    // A4 önizleme yalnız ilk ONIZLEME_TAVANI karneyi basar: yüzlerce karnelik önizleme sunucuda boşuna gigabaytlarca bellek tüketiyordu
    const previewIds = orderedSelectedIds().slice(0, ONIZLEME_TAVANI);
    const a4PreviewKey = `${selectedExam?.id || ''}|${resultsVersion}|${previewIds.join(',')}`;
    const renderA4Preview = () => (selectedExam ? (
        <div>
            {selectedStudentIds.size > ONIZLEME_TAVANI && (
                <div className="mb-2 rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">
                    Önizlemede seçili {selectedStudentIds.size} karnenin ilk {ONIZLEME_TAVANI} tanesi gösteriliyor; hepsi için "Detaylı Karne PDF" ile indirin.
                </div>
            )}
            <ReportCardPdfPreview
                cacheKey={a4PreviewKey}
                count={previewIds.length}
                load={() => ApiService.downloadPracticeExamReportCards(selectedExam.id, previewIds)}
            />
        </div>
    ) : null);

    const handleExamClick = async (exam: PracticeExam) => {
        try {
            setSelectedExam(exam);
            setResultsLoading(true);
            setViewMode('detail');
            setSelectedStudentIds(new Set());
            setSearchQuery("");
            await loadExamResults(exam);
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Sonuçlar yüklenirken hata oluştu', type: 'error' });
            setViewMode('list');
            setSelectedExam(null);
        } finally {
            setResultsLoading(false);
        }
    };

    const handleBack = () => {
        setViewMode('list');
        setSelectedExam(null);
        setSchoolFilter('');
        setSubeFilter('');
        setResultsData([]);
    };

    // Okul süzgeci (26 Eki — Mimar Sinan): satırın okulu sunucudan gelir (row.school = okul etiketi ya da öğrencinin okulun kayıtlı
    // öğrenci bölümünde olması); eski yanıtlarda okul etiketine düşülür
    const [schoolFilter, setSchoolFilter] = useState<string>('');
    const schoolOptions = useMemo(() => {
        const counts = new Map<string, { name: string; count: number }>();
        resultsData.forEach((row: any) => {
            const school = schoolOf(row);
            const id = school?.id || 'internal';
            counts.set(id, { name: school ? school.name : 'Kurum içi', count: (counts.get(id)?.count || 0) + 1 });
        });
        return Array.from(counts.entries()).map(([id, v]) => ({ id, ...v }));
    }, [resultsData]);
    // Şube süzgeci (26 Eki — TED: her şubenin listesi ayrı yazdırılabilsin). Sıralar okul kapsamındaki bütün katılımcılara göre
    // kalır; şube yalnız gösterilen/yazdırılan satırları süzer.
    const [subeFilter, setSubeFilter] = useState<string>('');
    const subeOf = (row: any) => row?.student?.department?.name || row?.results?.studentInfo?.class || '';
    const bySchoolRows = useMemo(() => (schoolFilter
        ? resultsData.filter((row: any) => (schoolOf(row)?.id || 'internal') === schoolFilter)
        : resultsData), [resultsData, schoolFilter]);
    const subeOptions = useMemo(() => {
        const counts = new Map<string, number>();
        bySchoolRows.forEach((row: any) => { const sube = subeOf(row); if (sube) counts.set(sube, (counts.get(sube) || 0) + 1); });
        return Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b, 'tr')).map(([name, count]) => ({ name, count }));
    }, [bySchoolRows]);
    const filteredStudents = useMemo(() => {
        const bySube = subeFilter ? bySchoolRows.filter((row: any) => subeOf(row) === subeFilter) : bySchoolRows;
        if (!debouncedSearch) return bySube;
        const lower = debouncedSearch.toLowerCase();
        return bySube.filter(row => {
            const studentInfo = row?.results?.studentInfo || {};
            const fullName = (row?.student?.firstName && row?.student?.lastName)
                ? `${row.student.firstName} ${row.student.lastName}`
                : (studentInfo.fullName || row?.student?.metadata?.parsedName || '');
            const tc = studentInfo.tc || row?.student?.metadata?.tcNumber || '';
            const schoolCode = studentInfo.schoolCode || '';
            return fullName.toLowerCase().includes(lower) ||
                tc.includes(lower) ||
                schoolCode.includes(lower);
        });
    }, [bySchoolRows, debouncedSearch, subeFilter]);
    const visibleStudentIds = useMemo(() => new Set(filteredStudents.map((row: any) => row.id)), [filteredStudents]);

    const toggleSelection = (id: string) => {
        const next = new Set(selectedStudentIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedStudentIds(next);
    };

    const toggleAll = () => {
        if (selectedStudentIds.size === filteredStudents.length) {
            setSelectedStudentIds(new Set());
        } else {
            setSelectedStudentIds(new Set(filteredStudents.map(s => s.id)));
        }
    };

    // Sayfanın kendisini basmak (window.print) uygulama düzeninin içinde kalıp yalnız ilk öğrenciyi basıyordu.
    const handlePrint = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }
        // Yazdırma penceresi tek seferde en çok KARNE_PARCA karne açar; fazlası (27 Eki TED: 57 karne "indirmiyor") parça parça indirilir
        if (selectedStudentIds.size > KARNE_PARCA) {
            await handleDownloadPdf();
            return;
        }
        try {
            setPrintLoading(true);
            const { blob } = await ApiService.downloadPracticeExamReportCards(selectedExam.id, Array.from(selectedStudentIds));
            printPdfBlob(blob);
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Karneler yazdırma için hazırlanamadı', type: 'error' });
        } finally {
            setPrintLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }

        let ids = orderedSelectedIds();
        try {
            setPdfLoading(true);
            setPdfParts([]);
            // Karneler başarı sırasıyla (29 Eki): sunucu her PDF'i kendi içinde sıralar; birden çok dosyada bölme de bu sırayla
            // olsun ki dosyalar baştan sona sırayı izlesin. Uç yoksa (eski sunucu) liste sırası kalır.
            if (ids.length > KARNE_PARCA) {
                try {
                    const secili = new Set(ids);
                    const sirali = (await ApiService.orderPracticeExamReportCards(selectedExam.id, ids)).resultIds.filter((id) => secili.has(id));
                    if (sirali.length === ids.length) ids = sirali;
                } catch {
                    /* liste sırası kalır */
                }
            }
            const parts: string[][] = [];
            for (let i = 0; i < ids.length; i += KARNE_PARCA) parts.push(ids.slice(i, i + KARNE_PARCA));
            if (parts.length === 1) {
                saveReportCardPdf(await ApiService.downloadPracticeExamReportCards(selectedExam.id, parts[0]));
            } else {
                // Sırayla: aynı anda tek istek (sunucu da sırayla basar); her parça ayrı dosya, sıra numaralı
                setPdfProgress({ done: 0, total: parts.length });
                const indirilen: { blob: Blob; fileName: string; label: string }[] = [];
                for (let i = 0; i < parts.length; i += 1) {
                    const file = await ApiService.downloadPracticeExamReportCards(selectedExam.id, parts[i]);
                    const from = i * KARNE_PARCA + 1;
                    const to = from + parts[i].length - 1;
                    const base = (file.fileName || 'Karneler.pdf').replace(/\.pdf$/i, '');
                    const part = { blob: file.blob, fileName: `${base}_${String(from).padStart(3, '0')}-${String(to).padStart(3, '0')}.pdf`, label: `${from}–${to}` };
                    indirilen.push(part);
                    saveReportCardPdf(part);
                    setPdfProgress({ done: i + 1, total: parts.length });
                }
                setPdfParts(indirilen);
            }
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Karne PDF\'i oluşturulurken hata oluştu', type: 'error' });
        } finally {
            setPdfLoading(false);
            setPdfProgress(null);
        }
    };

    // Kazanım bazlı hata kitapçığı yalnız Akademi sistemi YKS denemelerinde üretilir (kazanımlar cevap anahtarından gelir).
    const canDownloadErrorBooklet = selectedExam?.levelType === 'yks' && (selectedExam?.systemType === 'internal' || Boolean(selectedExam?.isInternal));

    const handleDownloadErrorBooklet = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }
        if (selectedStudentIds.size > 40) {
            await showAlert({ title: 'Uyarı', message: 'Hata kitapçığı tek seferde en çok 40 öğrenci için basılabilir.', type: 'warning' });
            return;
        }

        try {
            setBookletLoading(true);
            saveReportCardPdf(await ApiService.downloadPracticeExamErrorBooklets(selectedExam.id, Array.from(selectedStudentIds)));
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Hata kitapçığı oluşturulurken hata oluştu', type: 'error' });
        } finally {
            setBookletLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }

        const confirmed = await showConfirm({
            title: 'Seçili Sonuçları Sil',
            message: `${selectedStudentIds.size} öğrencinin bu denemedeki tüm cevapları ve sonucu silinecek. Bu işlem geri alınamaz.`,
            confirmText: 'Sil',
            cancelText: 'Vazgeç',
            confirmButtonVariant: 'danger',
        });

        if (!confirmed) return;

        try {
            setBulkActionLoading('delete');
            await ApiService.bulkDeletePracticeExamResults(selectedExam.id, Array.from(selectedStudentIds));
            setSelectedStudentIds(new Set());
            await loadExamResults(selectedExam);
            await showAlert({ title: 'Başarılı', message: 'Seçili öğrencilerin cevapları silindi.', type: 'success' });
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Seçili sonuçlar silinirken hata oluştu', type: 'error' });
        } finally {
            setBulkActionLoading(null);
        }
    };

    const handleBulkRegenerate = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }

        const confirmed = await showConfirm({
            title: 'Seçili Sonuçları Tekrar Oluştur',
            message: `${selectedStudentIds.size} öğrencinin mevcut cevapları korunarak sonuçları yeniden hesaplanacak.`,
            confirmText: 'Tekrar Oluştur',
            cancelText: 'Vazgeç',
            confirmButtonVariant: 'primary',
        });

        if (!confirmed) return;

        try {
            setBulkActionLoading('regenerate');
            await ApiService.bulkRegeneratePracticeExamResults(selectedExam.id, Array.from(selectedStudentIds));
            await loadExamResults(selectedExam);
            await showAlert({ title: 'Başarılı', message: 'Seçili öğrencilerin sonuçları yeniden oluşturuldu.', type: 'success' });
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Seçili sonuçlar yeniden oluşturulurken hata oluştu', type: 'error' });
        } finally {
            setBulkActionLoading(null);
        }
    };

    if (viewMode === 'list') {
        return (
            <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Karne Modülü</h2>
                        <p className="text-sm text-gray-500">Sonuçları yüklenmiş denemelerin karnelerini oluşturun ve indirin.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : exams.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-12 text-center border-2 border-dashed border-gray-200">
                        <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">Henüz Sonuçlanan Deneme Yok</h3>
                        <p className="text-gray-500 mt-1">Karne oluşturmak için önce "Tüm Denemeler" sekmesinden sonuç yükleyiniz.</p>
                    </div>
                ) : (
                    <>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative min-w-0 flex-[1_1_16rem]">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={examSearch}
                                onChange={(e) => setExamSearch(e.target.value)}
                                placeholder="Deneme ara"
                                className="w-full min-w-0 rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            />
                        </div>
                        <div className="inline-flex shrink-0 items-center rounded-lg border border-gray-300 bg-white p-1">
                            {([['', 'Tümü'], ['yks', 'YKS'], ['lgs', 'LGS']] as const).map(([key, label]) => {
                                const count = key ? exams.filter((exam) => exam.levelType === key).length : exams.length;
                                if (key && !count) return null;
                                return (
                                    <button
                                        key={key || 'all'}
                                        type="button"
                                        onClick={() => setExamType(key)}
                                        className={`rounded-md px-3 py-1.5 text-sm transition-colors ${examType === key ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}>
                                        {label} <span className="text-xs opacity-70">{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {visibleExams.length === 0 && (
                        <p className="py-8 text-center text-sm text-gray-500">Aramaya uyan deneme yok.</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {visibleExams.map((exam) => (
                            <div
                                key={exam.id}
                                onClick={() => handleExamClick(exam)}
                                className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Award className="w-16 h-16 text-blue-600 transform rotate-12" />
                                </div>

                                <h3 className="text-lg font-semibold text-gray-800  line-clamp-2" title={exam.name}>
                                    {exam.name}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                        {new Date(exam.date || '').toLocaleDateString('tr-TR')}
                                    </span>
                                    {exam.levelType && (
                                        <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-medium">
                                            {exam.levelType === 'yks' ? 'YKS' : 'LGS'}
                                        </span>
                                    )}
                                    {exam.audience && exam.audience !== 'internal' && (
                                        <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs font-medium">
                                            {exam.audience === 'external' ? 'Kurum dışı' : 'Kurum içi + dışı'}{exam.gradeLevel ? ` · ${exam.gradeLevel}. sınıf` : ''}
                                        </span>
                                    )}
                                </p>

                                <div className="flex items-center text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform">
                                    Raporları Görüntüle
                                    <ChevronLeft className="w-4 rotate-180 ml-1" />
                                </div>
                            </div>
                        ))}
                    </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-[calc(100vh-200px)] overflow-y-auto md:h-[calc(100vh-200px)] md:overflow-hidden">

            <div className="flex flex-col gap-3 p-4 border-b border-gray-200 bg-white sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
                    <button
                        onClick={handleBack}
                        className="shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <div className="min-w-0">
                        <h2 className="text-base font-bold leading-tight text-gray-800 break-words sm:text-lg">{selectedExam?.name}</h2>
                        <p className="text-xs text-gray-500">Raporlar ve Karne İşlemleri</p>
                    </div>
                </div>

                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end">
                    <button
                        onClick={handleBulkRegenerate}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                    >
                        {bulkActionLoading === 'regenerate' ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
                        <span>Tekrar Oluştur</span>
                    </button>
                    <button
                        onClick={handleBulkDelete}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                    >
                        {bulkActionLoading === 'delete' ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        <span>Cevapları Sil</span>
                    </button>
                    <button
                        onClick={() => setPreviewMode(previewMode === 'system' ? 'a4' : 'system')}
                        disabled={selectedStudentIds.size === 0}
                        className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2 ${
                            previewMode === 'a4'
                                ? 'bg-purple-600 text-white hover:bg-purple-700'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                        title={previewMode === 'a4' ? 'Sistem görünümüne dön' : 'Basılacak detaylı karnenin A4 önizlemesi'}
                    >
                        {previewMode === 'a4' ? <Monitor size={16} /> : <Eye size={16} />}
                        <span className="hidden sm:inline">{previewMode === 'a4' ? 'Sistem Görünümü' : 'A4 Önizleme'}</span>
                        <span className="sm:hidden">{previewMode === 'a4' ? 'Sistem' : 'A4'}</span>
                    </button>
                    <button
                        onClick={handleDownloadPdf}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null || pdfLoading}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                        title="Seçili öğrencilerin detaylı karne analizini tek PDF olarak indir"
                    >
                        {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                        <span className="sm:hidden">{pdfProgress ? `${pdfProgress.done}/${pdfProgress.total}` : 'Karne PDF'}</span>
                        <span className="hidden sm:inline">{pdfProgress ? `Karne PDF ${pdfProgress.done}/${pdfProgress.total} parça` : 'Detaylı Karne PDF'}</span>
                    </button>
                    {canDownloadErrorBooklet && (
                        <button
                            onClick={handleDownloadErrorBooklet}
                            disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null || bookletLoading}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                            title="Seçili öğrencilerin denemede kaçırdığı kazanımlardan çalışma kitapçığı (PDF)"
                        >
                            {bookletLoading ? <Loader2 size={16} className="animate-spin" /> : <BookX size={16} />}
                            <span>Hata Kitapçığı</span>
                        </button>
                    )}
                    <button
                        onClick={handlePrint}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null || printLoading || pdfLoading}
                        className="col-span-2 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                    >
                        {printLoading || (pdfLoading && selectedStudentIds.size > KARNE_PARCA) ? <Loader2 size={16} className="animate-spin" /> : selectedStudentIds.size > KARNE_PARCA ? <FileDown size={16} /> : <Printer size={16} />}
                        <span className="sm:hidden">{selectedStudentIds.size > 0 ? (selectedStudentIds.size > KARNE_PARCA ? `${selectedStudentIds.size} Seçiliyi İndir` : `${selectedStudentIds.size} Seçiliyi Yazdır`) : 'Seçim Yapınız'}</span>
                        <span className="hidden sm:inline">{selectedStudentIds.size > 0 ? (selectedStudentIds.size > KARNE_PARCA ? `${selectedStudentIds.size} Seçiliyi İndir (${Math.ceil(selectedStudentIds.size / KARNE_PARCA)} dosya)` : `${selectedStudentIds.size} Seçiliyi Yazdır`) : 'Seçim Yapınız'}</span>
                    </button>
                </div>
            </div>

            {pdfParts.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 sm:px-4 sm:text-sm">
                    <span>Karneler {pdfParts.length} dosya hâlinde indirildi. Tarayıcı bir dosyayı engellediyse tıklayıp yeniden indirin:</span>
                    {pdfParts.map((p, i) => (
                        <button
                            key={p.fileName}
                            onClick={() => saveReportCardPdf(p)}
                            className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 font-medium text-indigo-700 hover:bg-indigo-100"
                        >
                            <FileDown size={14} /> {i + 1}. dosya ({p.label})
                        </button>
                    ))}
                    <button onClick={() => setPdfParts([])} className="ml-auto text-indigo-700 hover:text-indigo-900">Kapat</button>
                </div>
            )}

            <div className="flex flex-col overflow-visible md:flex-1 md:flex-row md:overflow-hidden">

                <div className="flex w-full flex-col bg-gray-50 border-b border-gray-200 md:w-1/3 md:border-b-0 md:border-r lg:w-1/4">
                    <div className="p-3 border-b border-gray-200 bg-white">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4" />
                            <input
                                type="text"
                                placeholder="Öğrenci ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                        </div>
                        {schoolOptions.length > 1 && (
                            <select
                                value={schoolFilter}
                                onChange={(e) => { setSchoolFilter(e.target.value); setSelectedStudentIds(new Set()); }}
                                className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                                aria-label="Okul">
                                <option value="">Bütün okullar ({resultsData.length})</option>
                                {schoolOptions.map((school) => <option key={school.id} value={school.id}>{school.name} ({school.count})</option>)}
                            </select>
                        )}
                        {subeOptions.length > 1 && (
                            <select
                                value={subeFilter}
                                onChange={(e) => { setSubeFilter(e.target.value); setSelectedStudentIds(new Set()); }}
                                className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                                aria-label="Şube">
                                <option value="">Bütün şubeler ({bySchoolRows.length})</option>
                                {subeOptions.map((sube) => <option key={sube.name} value={sube.name}>{sube.name} ({sube.count})</option>)}
                            </select>
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-xs text-gray-500">
                            <span>{filteredStudents.length} öğrenci bulundu</span>
                            <button onClick={toggleAll} className="shrink-0 text-blue-600 hover:underline">
                                {selectedStudentIds.size === filteredStudents.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                            </button>
                        </div>
                    </div>

                    <div className="p-2 space-y-1 md:flex-1 md:overflow-y-auto">
                        {resultsLoading && (
                            <div className="p-4 text-center text-gray-500 text-sm">Yükleniyor...</div>
                        )}
                        {!resultsLoading && filteredStudents.map(student => {
                            const isSelected = selectedStudentIds.has(student.id);
                            const studentInfo = student?.results?.studentInfo || {};
                            const fullName = (student?.student?.firstName && student?.student?.lastName)
                                ? `${student.student.firstName} ${student.student.lastName}`
                                : (studentInfo.fullName || student?.student?.metadata?.parsedName || 'İsimsiz Öğrenci');

                            return (
                                <div
                                    key={student.id}
                                    onClick={() => toggleSelection(student.id)}
                                    className={`flex items-start gap-3 rounded-lg border p-3 transition-all sm:items-center cursor-pointer ${isSelected
                                        ? 'bg-blue-50 border-blue-200 shadow-sm'
                                        : 'bg-white border-transparent hover:bg-gray-100 hover:border-gray-200'
                                        }`}
                                >
                                    <div className={`flex-shrink-0 text-blue-600`}>
                                        {isSelected ? <CheckSquare size={18} /> : <Square size={18} className="text-gray-400" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                            {fullName}
                                        </p>
                                        <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <span>{student?.student?.department?.name || 'Sınıf Yok'}</span>
                                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                            <span>{student?.student?.studentNumber || student?.schoolCode || studentInfo?.schoolCode || '-'}</span>
                                        </p>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <span className={`text-xs font-bold ${listSuccess(student) >= 50 ? 'text-green-600' : 'text-red-500'
                                            }`}>
                                            %{listSuccess(student).toFixed(0)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-gray-100 p-3 md:hidden">
                    {selectedStudentIds.size > 0 ? (
                        previewMode === 'a4' ? (
                            !isDesktopViewport && renderA4Preview()
                        ) : (
                            <div className="overflow-x-auto overflow-y-visible rounded-2xl bg-white shadow-sm">
                                <div className="min-w-[980px]">
                                    {(() => {
                                        const firstId = Array.from(selectedStudentIds)[0];
                                        const studentData = resultsData.find(s => s.id === firstId);
                                        if (!studentData) return null;

                                        const data = getStudentReportData(studentData, examMetadata);
                                        return <WebStudentReportCard data={data} examName={selectedExam?.name} companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} onAnalysisSaved={handleAnalysisSaved} />;
                                    })()}
                                </div>
                            </div>
                        )
                    ) : (
                        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                            Önizleme görmek için bir öğrenci seçin.
                        </div>
                    )}
                </div>

                <div className="hidden md:flex flex-1 bg-gray-100 p-8 overflow-auto justify-center items-start">
                    {selectedStudentIds.size > 0 ? (
                        previewMode === 'a4' ? (
                            isDesktopViewport && renderA4Preview()
                        ) : (
                            <div
                                className="bg-white shadow-2xl rounded-sm flex flex-col overflow-hidden"
                                style={{ width: '297mm', minHeight: '210mm', padding: '10mm' }}
                            >
                                {(() => {
                                    const firstId = Array.from(selectedStudentIds)[0];
                                    const studentData = resultsData.find(s => s.id === firstId);
                                    if (!studentData) return null;

                                    const data = getStudentReportData(studentData, examMetadata);
                                    return <WebStudentReportCard data={data} examName={selectedExam?.name} companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} onAnalysisSaved={handleAnalysisSaved} />;
                                })()}
                            </div>
                        )
                    ) : (
                        <ExamResultList
                            data={bySchoolRows}
                            visibleIds={visibleStudentIds}
                            subtitle={subeFilter ? `${subeFilter} Şubesi` : undefined}
                            metadata={examMetadata}
                            examName={selectedExam?.name}
                            examDate={selectedExam?.date}
                            companyLogo={systemConfig?.companyLogo || undefined}
                            companyName={systemConfig?.companyName || undefined}
                        />
                    )}
                </div>
            </div>

        </div>
    );
}

export const getStudentReportData = (studentRow: any, examMetadata: any) => {
    const stats = studentRow.stats || {};
    const answers = studentRow.answers || {};
    const booklets = examMetadata.bookletMapping || {};
    const testNames = examMetadata.testNames || {};
    const testAverages = examMetadata.testAverages || {};
    // LGS'de 3 yanlış 1 doğruyu, YKS'de 4 yanlış 1 doğruyu götürür.
    const netPenalty = examMetadata.levelType === 'lgs' ? 1 / 3 : 0.25;

    const subjects = Object.entries(answers).map(([testId, data]: [string, any]) => {
        // LGS anahtarında test adı "Sözel/Sayısal"; tek dersli testte ders adı gösterilir.
        const lgsSubject = examMetadata.levelType === 'lgs' && Array.isArray(data.subjects) && data.subjects.length === 1 ? data.subjects[0]?.name : null;
        const name = lgsSubject || testNames[testId] || data.testName || `Test ${testId}`;
        const avgData = testAverages[testId] || {};

        return {
            name,
            testId,
            totalQ: data.totalQuestions || 0,
            correct: data.correct || 0,
            incorrect: data.incorrect || 0,
            empty: data.unanswered || 0,
            net: data.net || (data.correct - (data.incorrect * netPenalty)),
            subjects: data.subjects || [],
            questions: data.questions || [],
            avgNet: avgData.avgNet || 0,
            avgSuccessRate: avgData.successRate || 0
        };
    });

    const allQuestions: any[] = [];
    Object.entries(answers).forEach(([testId, data]: [string, any]) => {
        const testName = testNames[testId] || data.testName || `Test ${testId}`;
        (data.questions || []).forEach((q: any) => {
            allQuestions.push({
                ...q,
                testId,
                testName
            });
        });
    });

    const achievements: any[] = [];
    allQuestions.forEach(q => {
        if (q.achievement) {
            const existing = achievements.find(a => a.name === q.achievement && a.subject === (q.subjectName || q.testName));
            if (existing) {
                existing.total++;
                if (q.isCorrect) existing.correct++;
                else if (q.isAnswered) existing.incorrect++;
            } else {
                achievements.push({
                    subject: q.subjectName || q.testName,
                    name: q.achievement,
                    total: 1,
                    correct: q.isCorrect ? 1 : 0,
                    incorrect: q.isAnswered && !q.isCorrect ? 1 : 0
                });
            }
        }
    });

    return {
        // Yapay zekâ analizi bu sonuca kaydedilir (WebStudentReportCard)
        resultId: studentRow.id,
        examId: studentRow.practiceExamId,
        student: studentRow.student,
        info: studentRow.results?.studentInfo || {},
        stats,
        subjects,
        allQuestions,
        achievements,
        bookletType: studentRow.results?.studentInfo?.bookletType || 'A',
        schoolCode: studentRow.schoolCode,
        testAverages: testAverages,
        overallStats: examMetadata.overallStats || {},
        // Yayınevi denemesinde alan puanı (SAY/EA/SÖZ + alan sırası); ayar kapanınca sunucu kaydı siler
        alanPuani: studentRow.results?.alanPuani?.alan ? studentRow.results.alanPuani : null,
        examConfig: {
            levelType: examMetadata.levelType || null,
            yksMode: examMetadata.yksMode || null,
            // Akademi sistemi denemesinde test numaraları sabit düzendedir (YKS: 1-4 TYT, 5-8 AYT)
            systemType: examMetadata.systemType || null,
        }
    };
};

export const StudentReportCard = ({ data, examName, isPrint, companyLogo, companyName, totalStudents = 0 }: { data: any, examName?: string, isPrint?: boolean, companyLogo?: string, companyName?: string, totalStudents?: number }) => {
    const { student, info, stats, subjects, bookletType, schoolCode, allQuestions = [], achievements = [], testAverages = {}, overallStats = {}, examConfig = {} } = data;

    const safeStr = (s: any) => s || '';
    const safeNum = (n: any) => Number(n) || 0;
    const fmt = (n: any) => typeof n === 'number' ? n.toFixed(2) : '0.00';

    const fullName = (student?.firstName && student?.lastName)
        ? `${student.firstName} ${student.lastName}`
        : (info.fullName || student?.metadata?.parsedName || 'Öğrenci');

    const studentNo = '*****';
    const sube = student?.department?.name || student?.metadata?.className || info?.class || '-';
    const rawTc = student?.metadata?.tcNumber || '';
    const tcNo = rawTc && rawTc.length >= 6 ? rawTc.substring(0, 6) + '*****' : (rawTc || '***********');

    const examTitle = examName || "TÜRKİYE GENELİ TYT İLK PROVA (T1225)";

    const findSub = (name: string) => subjects.find((s: any) => s.name?.toLowerCase().includes(name.toLowerCase())) || { totalQ: 0, correct: 0, incorrect: 0, empty: 0, net: 0, questions: [] };

    const normalizeExamLabel = (value: string) => (value || '')
        .toLowerCase()
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const normalizeBranchSubject = (value: string) => normalizeExamLabel(value || '');
    const branchSelections = info?.branchSelections || student?.metadata?.branchSelections || {};

    const filterAlternativeBranchQuestions = (questions: any[]) => {
        return questions.filter((question: any) => {
            const testId = Number(question?.testId || 0);
            const subject = normalizeBranchSubject(question?.subjectName || '');

            if (testId === 2) {
                const selected = branchSelections?.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din';
                if (selected === 'felsefe') return !subject.includes('din kulturu');
                return !subject.includes('ilave felsefe');
            }

            if (testId === 6) {
                const selected = branchSelections?.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din';
                if (selected === 'felsefe') return !subject.includes('din kulturu');
                return !subject.includes('ilave felsefe');
            }

            return true;
        });
    };

    const getColumnRank = (name: string, totalQ: number, originalIndex: number) => {
        const normalized = normalizeExamLabel(name);

        if (normalized === 'turkce') return 1;
        if (normalized === 'sosyal bilimler') return originalIndex >= 4 ? 6 : 2;
        if (normalized === 'temel matematik') return 3;
        if (normalized === 'fen bilimleri') return totalQ > 20 ? 8 : 4;
        if (normalized.includes('turk dili ve edebiyati') || normalized.includes('edebiyati sosyal bilimler 1')) return 5;
        if (normalized === 'sosyal bilimler 2') return 6;
        if (normalized === 'matematik') return 7;

        return 100 + originalIndex;
    };

    const getCompactTestLabel = (name: string) => {
        const normalized = normalizeExamLabel(name);

        if (normalized === 'turkce') return 'TÜRKÇE';
        if (normalized === 'sosyal bilimler') return 'SOSYAL BİL.';
        if (normalized === 'temel matematik') return 'TEMEL MAT.';
        if (normalized === 'fen bilimleri') return 'FEN BİL.';
        if (normalized.includes('turk dili ve edebiyati') || normalized.includes('edebiyati sosyal bilimler 1')) {
            return 'TÜRK D. VE EDEB.';
        }
        if (normalized === 'sosyal bilimler 2') return 'SOSYAL BİL.-2';
        if (normalized === 'matematik') return 'MATEMATİK';

        return (name || 'TEST').toUpperCase().substring(0, 16);
    };

    const filteredQuestions = filterAlternativeBranchQuestions(allQuestions);
    const filteredQuestionMap = filteredQuestions.reduce((acc: Record<string, any[]>, question: any) => {
        const key = String(question?.testId || '');
        if (!acc[key]) acc[key] = [];
        acc[key].push(question);
        return acc;
    }, {});

    type ReportColumn = {
        header: string;
        fullHeader: string;
        testId: string | number;
        data: {
            totalQ: number;
            correct: number;
            incorrect: number;
            empty: number;
            net: number;
            avgNet: number;
            avgSuccessRate: number;
        };
        questions: any[];
        originalIndex: number;
    };

    const netPenalty = examConfig?.levelType === 'lgs' ? 1 / 3 : 0.25;

    const columns: ReportColumn[] = subjects.map((s: any, index: number) => {
        const scopedQuestions = [...(filteredQuestionMap[String(s.testId)] || s.questions || [])].sort((left: any, right: any) => {
            const leftOrder = Number(left?.questionNumber || 0);
            const rightOrder = Number(right?.questionNumber || 0);
            return leftOrder - rightOrder;
        });
        const totalQ = scopedQuestions.length || s.totalQ || 0;
        const correct = scopedQuestions.filter((question: any) => question.isCorrect).length;
        const incorrect = scopedQuestions.filter((question: any) => question.isAnswered && !question.isCorrect).length;
        const empty = scopedQuestions.filter((question: any) => !question.isAnswered).length;
        const net = correct - (incorrect * netPenalty);

        return {
            header: getCompactTestLabel(s.name || ''),
            fullHeader: s.name || `Test ${index + 1}`,
            testId: s.testId,
            data: {
                totalQ,
                correct,
                incorrect,
                empty,
                net,
                avgNet: s.avgNet || 0,
                avgSuccessRate: s.avgSuccessRate || 0
            },
            questions: scopedQuestions,
            originalIndex: index,
        };
    }).sort((left: ReportColumn, right: ReportColumn) => {
        return getColumnRank(left.fullHeader, left.data.totalQ, left.originalIndex) - getColumnRank(right.fullHeader, right.data.totalQ, right.originalIndex);
    });

    const scoring = stats?.scoring || {};
    const scoreTypes = scoring?.scoreTypes || {};
    const primaryScore = scoring?.primaryScore || null;
    const sessionScores = scoring?.sessionScores || {};
    const sessionRankings = scoring?.sessionRankings || {};
    const getScoreTypeValue = (scoreType: string) => {
        const profile = scoreTypes?.[scoreType];
        return Number((profile?.score ?? profile?.rawScore) || 0).toFixed(3);
    };
    const yksAytScoreColumns = [
        { key: 'SAY', label: 'Sayısal', score: getScoreTypeValue('SAY') },
        { key: 'EA', label: 'Eşit Ağırlık', score: getScoreTypeValue('EA') },
        { key: 'SOZ', label: 'Sözel', score: getScoreTypeValue('SOZ') },
    ].filter((item) => scoreTypes?.[item.key]);
    const normalizedHeaders = columns.map((column: ReportColumn) => normalizeExamLabel(column.fullHeader));
    const ykstSessionLabels = [
        sessionScores?.labels?.primary,
        sessionScores?.labels?.secondary,
        sessionScores?.labels?.total,
    ]
        .filter(Boolean)
        .map((label: string) => normalizeExamLabel(label));
    const normalizedYksTytHeaders = ['turkce', 'sosyal bilimler', 'temel matematik', 'fen bilimleri'];
    const hasOnlyYksTytHeaders = normalizedHeaders.length > 0
        && normalizedHeaders.every((header: string) => normalizedYksTytHeaders.includes(header));
    const hasYksAytHeaders = normalizedHeaders.some((header: string) => header.includes('turk dili ve edebiyati') || header.includes('edebiyati sosyal bilimler 1'));
    const isLikelyYKS = examConfig?.levelType === 'yks'
        || examConfig?.yksMode === 'single_tyt'
        || ykstSessionLabels.includes('tyt')
        || (normalizedHeaders.includes('turkce')
            && normalizedHeaders.includes('temel matematik')
            && (hasYksAytHeaders || hasOnlyYksTytHeaders));

    const groupDefinitions = isLikelyYKS
        ? [Math.min(4, columns.length), Math.max(columns.length - 4, 0)].filter((size) => size > 0)
        : [4, 2, 3, 3, 7, 1];

    interface SessionGroup {
        id: number;
        colSpan: number;
        startIndex: number;
        columns: any[];
    }

    const sessionGroups: SessionGroup[] = [];
    let currentIndex = 0;

    groupDefinitions.forEach((size, idx) => {
        if (currentIndex < columns.length) {
            const count = Math.min(size, columns.length - currentIndex);
            if (count > 0) {
                sessionGroups.push({
                    id: idx + 1,
                    colSpan: count,
                    startIndex: currentIndex,
                    columns: columns.slice(currentIndex, currentIndex + count)
                });
                currentIndex += count;
            }
        }
    });

    while (currentIndex < columns.length) {
        sessionGroups.push({
            id: sessionGroups.length + 1,
            colSpan: 1,
            startIndex: currentIndex,
            columns: [columns[currentIndex]]
        });
        currentIndex++;
    }

    const rowLabels = [
        { label: 'SORU SAYISI', key: 'totalQ', fmt: (v: any) => v },
        { label: 'DOĞRU CEVAP', key: 'correct', fmt: (v: any) => v },
        { label: 'YANLIŞ CEVAP', key: 'incorrect', fmt: (v: any) => v },
        { label: 'BOŞ CEVAP', key: 'empty', fmt: (v: any) => v },
        { label: 'NET', key: 'net', fmt: (v: any) => Number(v).toFixed(2) },
        { label: 'BAŞARI ORANI', key: 'success', fmt: (v: any) => `%${v}` },
        { label: 'SINAV NET ORT.', key: 'avg', fmt: (v: any) => Number(v).toFixed(2) },
    ];

    const getColData = (col: any, key: string) => {
        if (key === 'success') return col.data.totalQ > 0 ? ((col.data.net / col.data.totalQ) * 100).toFixed(0) : '0';
        if (key === 'avg') return Number(col.data.avgNet || 0).toFixed(2);

        return col.data[key];
    };

    // Yalnız AYT denemesinde (11. sınıf alan denemesi) TYT oturumu ve TYT puanı yok
    const isSingleAyt = examConfig?.yksMode === 'single_ayt';
    const yksSessionColumns = isLikelyYKS ? [
        ...(isSingleAyt ? [] : [{ key: 'TYT', group: 'TYT', label: sessionScores?.labels?.primary || 'TYT', score: Number((sessionScores?.tytScore ?? sessionScores?.session1Score) || 0).toFixed(3) }]),
        ...(yksAytScoreColumns.length > 0
            ? yksAytScoreColumns.map((item) => ({ ...item, group: 'AYT' }))
            : sessionScores?.labels?.secondary
                ? [{ key: 'AYT', group: 'AYT', label: sessionScores.labels.secondary, score: Number((sessionScores?.aytScore ?? sessionScores?.session2Score) || 0).toFixed(3) }]
                : []),
    ] : [];
    const nonScoreSessionLabels = isLikelyYKS
        ? isSingleAyt ? ['AYT'] : [
            sessionScores?.labels?.primary || 'TYT',
            ...(yksAytScoreColumns.length > 0 || sessionScores?.labels?.secondary ? [sessionScores?.labels?.secondary || 'AYT'] : []),
        ]
        : ['1. Oturum', '2. Oturum'];
    const scoreGridColumns = sessionGroups;

    const formatRank = (value: any) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric > 0 ? numeric.toLocaleString('tr-TR') : '-';
    };

    const getYksRankValue = (column: any, scope: 'turkiye' | 'kurum' | 'sube') => {
        const scoreType = column?.key;
        if (scope === 'turkiye') {
            // Yalnız ÖSYM puan dağılımından gelen tahmin basılır; eski kayıtlardaki "kurum içi yüzdelik × nüfus" basılmaz.
            const estimate = scoreType === 'TYT'
                ? (sessionRankings?.tyt?.estimatedNational || scoreTypes?.TYT?.rankings?.estimatedNational)
                : scoreTypes?.[scoreType]?.rankings?.estimatedNational;
            return estimate?.source ? formatRank(estimate.estimatedRank) : '-';
        }

        if (scoreType === 'TYT') {
            const ranking = scope === 'kurum'
                ? sessionRankings?.tyt?.general
                : sessionRankings?.tyt?.school;
            return formatRank(ranking?.rank);
        }

        const ranking = scope === 'kurum'
            ? scoreTypes?.[scoreType]?.rankings?.general
            : scoreTypes?.[scoreType]?.rankings?.school;
        return formatRank(ranking?.rank);
    };

    const getScoreColor = (score: number | string, sessionCount: number = 1) => {
        const numScore = typeof score === 'string' ? parseFloat(score) : score;
        const redThreshold = 300 / sessionCount;
        const orangeThreshold = 400 / sessionCount;

        if (numScore < redThreshold) return 'text-red-600';
        if (numScore < orangeThreshold) return 'text-orange-500';
        return 'text-green-600';
    };

    const getQuestionsForSubject = (subjectName: string) => {
        return filteredQuestions.filter((q: any) =>
            (q.testName || q.subjectName || '').toLowerCase().includes(subjectName.toLowerCase())
        );
    };

    const buildAnswerStrip = (questions: any[]) => ({
        correctAnswers: (questions || []).map(q => q.correctAnswer || ''),
        studentAnswers: (questions || []).map(q => q.studentAnswer || '')
    });

    const answerStrips = columns.slice(0, 8).map((col: any) => ({
        name: col.header,
        ...buildAnswerStrip(col.questions || [])
    }));

    const filteredAchievements = (() => {
        const rebuilt: any[] = [];
        const sourceQuestions = filteredQuestions.filter((question: any) => (
            question.achievement
            && question.isAnswered
            && !question.isCorrect
        ));

        sourceQuestions.forEach((question: any) => {
            const subject = question.subjectName || question.testName || 'Genel';
            const existing = rebuilt.find((item) => item.subject === subject && item.name === question.achievement);
            if (existing) {
                existing.total += 1;
                existing.incorrect += 1;
                return;
            }

            rebuilt.push({
                subject,
                name: question.achievement,
                total: 1,
                correct: 0,
                incorrect: 1,
            });
        });

        return rebuilt.sort((left: any, right: any) => {
            const subjectCompare = (left.subject || '').localeCompare((right.subject || ''), 'tr');
            if (subjectCompare !== 0) return subjectCompare;
            return (left.name || '').localeCompare((right.name || ''), 'tr');
        });
    })();

    let allTopics = filteredAchievements.length > 0
        ? filteredAchievements.map((a: any) => ({
            subject: a.subject?.toUpperCase() || 'GENEL',
            name: a.name || 'Kazanım',
            total: a.total || 0,
            correct: a.correct || 0,
            incorrect: a.incorrect || 0,
            percent: a.total > 0 ? Math.round((a.correct / a.total) * 100) : 0
        }))
        :
        subjects.flatMap((s: any) =>
            (s.subjects || []).slice(0, 10).map((sub: any) => ({
                subject: s.name?.toUpperCase() || 'TEST',
                name: sub.name || 'Alt Ders',
                total: sub.totalQuestions || 0,
                correct: sub.correct || 0,
                incorrect: sub.incorrect || 0,
                percent: sub.percentage || 0
            }))
        );

    const topicColumnSize = Math.max(1, Math.ceil(allTopics.length / 3));
    const topicCols = [
        allTopics.slice(0, topicColumnSize),
        allTopics.slice(topicColumnSize, topicColumnSize * 2),
        allTopics.slice(topicColumnSize * 2)
    ].map((topics) => topics.length > 0 ? topics : [{ subject: '', name: '', total: 0, correct: 0, incorrect: 0, percent: 0 }]);

    return (
        <div className="flex flex-col w-full h-full bg-white text-[10px] font-sans leading-tight p-2" style={{ fontFamily: 'Arial Narrow, Arial, sans-serif', minHeight: '287mm' }}>

            <div className="flex flex-col">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">

                        <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                            {companyLogo ? (
                                <img src={companyLogo} alt={companyName || 'Logo'} className="w-full h-full object-contain" />
                            ) : (
                                <span className="text-[9px] text-gray-400">LOGO</span>
                            )}
                        </div>
                        <div className="font-bold text-[10px] text-gray-800 whitespace-nowrap">{companyName || 'İSTANBUL / ESENLER / SAYE EĞİTİM'}</div>
                    </div>
                    <div>
                        <div className="font-bold text-[12px] text-blue-600 uppercase tracking-tight">SINAV SONUÇ BELGESİ</div>
                    </div>
                </div>
                <div className="text-center w-full">
                    <div className="text-[8px] italic text-gray-500 mb-1.5">
                        * Türkiye sırası, ham puanın ÖSYM 2026-YKS puan dağılımındaki yerine göre tahmini sıradır.
                    </div>
                </div>
            </div>

            <div className="flex border border-black text-center  text-[9px]">

                <div className="flex w-[30%]">
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">KİTAPÇIK</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{bookletType || 'A'}</div>
                    </div>
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">ŞUBE</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{sube}</div>
                    </div>
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">ÖĞR.NO</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{studentNo}</div>
                    </div>
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">TC NO</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{tcNo}</div>
                    </div>
                </div>

                <div className="w-[20%] border-r border-black overflow-hidden">
                    <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">ADI - SOYADI</div>
                    <div className="bg-[#F2F2F2] py-0.5 font-bold uppercase whitespace-nowrap truncate px-1">{fullName}</div>
                </div>

                <div className="flex-1 border-r border-black overflow-hidden">
                    <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">SINAV ADI</div>
                    <div className="bg-[#F2F2F2] py-0.5 font-bold uppercase whitespace-nowrap truncate px-1">{examTitle}</div>
                </div>

                <div className="w-20">
                    <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">TARİH</div>
                    <div className="bg-[#F2F2F2] py-0.5 font-bold">{new Date().toLocaleDateString('tr-TR')}</div>
                </div>
            </div>

            <div className="overflow-hidden">
                <table className="w-full text-center border-collapse text-[8px] border border-black">
                    <thead>
                        <tr>
                            <th rowSpan={2} className="bg-[#F8CBAD] border border-black font-bold whitespace-nowrap" style={{ width: '4rem' }}>OTURUMLAR</th>

                            {sessionGroups.map((group, idx) => (
                                <th
                                    key={group.id}
                                    colSpan={group.colSpan}
                                    className={`bg-[#F8CBAD] border-t border-b border-black py-0.5 whitespace-nowrap ${idx === sessionGroups.length - 1 ? 'border-r border-black' : 'border-r-2 border-black'}`}
                                >
                                    {isLikelyYKS
                                        ? (isSingleAyt ? 'AYT' : group.id === 1 ? 'TYT' : group.id === 2 ? 'AYT' : `${group.id}. Bölüm`)
                                        : `${group.id}. Oturum`}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {columns.map((col: any, i: number) => {
                                const isGroupEnd = sessionGroups.some(g => g.startIndex + g.colSpan - 1 === i);
                                const isLastCol = i === columns.length - 1;
                                return (
                                    <th key={i} className={`bg-gray-200 border-b border-black py-0.5 px-0.5 font-bold whitespace-nowrap ${isLastCol ? 'border-r border-r-black' : isGroupEnd ? 'border-r-2 border-r-black' : 'border-r border-r-gray-400'}`}>{col.header}</th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rowLabels.map((row, rIdx) => (
                            <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="bg-[#F8CBAD] border-l border-r border-r-black border-b border-gray-400 font-bold text-left px-1 py-0.5 whitespace-nowrap">{row.label}</td>
                                {columns.map((col: any, cIdx: number) => {
                                    const isGroupEnd = sessionGroups.some(g => g.startIndex + g.colSpan - 1 === cIdx);
                                    const isLastCol = cIdx === columns.length - 1;
                                    return (
                                        <td key={cIdx} className={`border-b border-gray-300 py-0.5 whitespace-nowrap ${isLastCol ? 'border-r border-r-black' : isGroupEnd ? 'border-r-2 border-r-black' : 'border-r border-r-gray-300'}`}>{getColData(col, row.key)}</td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <table className="w-full border border-black text-[8px] border-collapse">
                <thead>

                    <tr className="border-b border-black">
                        <th colSpan={2} rowSpan={2} className="bg-[#F2F2F2] font-bold px-1 py-0.5 border-l border-r border-black text-left">PUANLAR</th>
                        {isLikelyYKS ? (
                            <>
                                {yksAytScoreColumns.length > 0 ? (
                                    <>
                                        <th className="bg-[#F8CBAD] font-bold px-1 py-0.5 text-center w-12 border-r border-gray-300">TYT</th>
                                        <th colSpan={yksAytScoreColumns.length} className="bg-[#F8CBAD] font-bold px-1 py-0.5 text-center border-r-2 border-black">AYT</th>
                                    </>
                                ) : (
                                    yksSessionColumns.map((item, idx) => (
                                        <th key={item.key} className={`bg-[#F8CBAD] font-bold px-1 py-0.5 text-center w-12 ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{item.label}</th>
                                    ))
                                )}
                                <th colSpan={nonScoreSessionLabels.length} className="bg-[#FFF2CC] font-bold px-1 py-0.5 border-r-2 border-black text-center">TESTE GÖRE ÖĞRENCİ SAYILARI</th>
                                <th className="bg-[#FFCCCC] font-bold px-1 py-0.5 text-center whitespace-nowrap" colSpan={nonScoreSessionLabels.length}>Kitapçık Türü</th>
                            </>
                        ) : (
                            <>
                                <th className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-gray-300 text-center w-12">Genel</th>
                                {sessionGroups.map((group, idx) => (
                                    <th key={group.id} className={`bg-[#F8CBAD] font-bold px-1 py-0.5 text-center w-10 ${idx === sessionGroups.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{group.id}. Oturum</th>
                                ))}
                                <th colSpan={sessionGroups.length} className="bg-[#FFF2CC] font-bold px-1 py-0.5 border-r-2 border-black text-center">TESTE GÖRE ÖĞRENCİ SAYILARI</th>
                                <th className="bg-[#FFCCCC] font-bold px-1 py-0.5 text-center whitespace-nowrap" colSpan={sessionGroups.length}>Kitapçık Türü</th>
                            </>
                        )}
                    </tr>

                    <tr style={{ height: "10px" }} className="border-b border-gray-300">
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((item, idx) => (
                                    <td key={item.key} className={`px-1 py-0.5 text-center font-bold ${getScoreColor(item.score)} ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>
                                        {item.group === 'AYT' && yksAytScoreColumns.length > 0 ? (
                                            <div className="leading-tight">
                                                <div className="text-[7px] text-gray-700">{item.label}</div>
                                                <div>{item.score}</div>
                                            </div>
                                        ) : item.score}
                                    </td>
                                ))}
                                {nonScoreSessionLabels.map((item, idx) => (
                                    <td key={`c-${item}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{item}</td>
                                ))}
                                {nonScoreSessionLabels.map((item, idx) => (
                                    <td key={`b-${item}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{item}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                <td className={`px-1 py-0.5 border-r border-gray-300 text-center font-bold ${getScoreColor(Number(primaryScore?.score || 0))}`}>{Number(primaryScore?.score || 0).toFixed(3)}</td>
                                {sessionGroups.map((group, idx) => (
                                    <td key={group.id} className={`px-1 py-0.5 text-center font-bold ${idx === sessionGroups.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>0.000</td>
                                ))}
                                {sessionGroups.map((group, idx) => (
                                    <td key={group.id} className={`px-1 py-0.5 text-center font-bold ${idx === sessionGroups.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{group.id}. Oturum</td>
                                ))}
                                {sessionGroups.map((group, idx) => (
                                    <td key={`b-${group.id}`} className={`px-1 py-0.5 text-center font-bold ${idx === sessionGroups.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{group.id}. Oturum</td>
                                ))}
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>

                    <tr className="border-b border-gray-200">
                        <td rowSpan={5} className="bg-gray-200 font-bold border-l border-r border-t border-black p-0 w-4 text-[9px] align-middle">
                            <div className="flex items-center justify-center w-full h-full" style={{ padding: '4px 0' }}>
                                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>DERECELER</div>
                            </div>
                        </td>
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-t border-black whitespace-nowrap" title="ÖSYM 2026-YKS puan dağılımına göre tahmini sıra">TÜRKİYE*</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`tr-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{getYksRankValue(g, 'turkiye')}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`trc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`trb-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{bookletType || 'A'}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`tr-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`trc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`trb-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{bookletType || 'A'}</td>
                                ))}
                            </>
                        )}
                    </tr>

                    <tr className="border-b border-gray-200">
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">İL</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`il-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`ilc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                <td colSpan={nonScoreSessionLabels.length} rowSpan={4} className="text-center align-middle border-l border-gray-300">
                                    <div className="font-bold text-[9px]">SINAVA GİREN ÖĞRENCİ</div>
                                    <div className="font-bold text-[9px]">SAYISI</div>
                                    <div className="text-sm font-bold mt-0.5">{totalStudents}</div>
                                </td>
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`il-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`ilc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                <td colSpan={scoreGridColumns.length} rowSpan={4} className="text-center align-middle border-l border-gray-300">
                                    <div className="font-bold text-[9px]">SINAVA GİREN ÖĞRENCİ</div>
                                    <div className="font-bold text-[9px]">SAYISI</div>
                                    <div className="text-sm font-bold mt-0.5">{totalStudents}</div>
                                </td>
                            </>
                        )}
                    </tr>

                    <tr className="border-b border-gray-200">
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">İLÇE</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`ilce-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`ilcec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`ilce-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`ilcec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        )}
                    </tr>

                    <tr className="border-b border-gray-200">
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">KURUM</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`kurum-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{getYksRankValue(g, 'kurum')}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`kurumc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`kurum-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`kurumc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        )}
                    </tr>

                    <tr>
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">ŞUBE</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`sube-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{getYksRankValue(g, 'sube')}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`subec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`sube-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`subec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        )}
                    </tr>
                </tbody>
            </table>

            <div className="flex items-start">

                <div className="w-1/2 border-r border-black">
                    <table className="w-full border border-black text-[7px] border-collapse">
                        <tbody>
                            {columns.slice(0, Math.ceil(columns.length / 2)).map((col: any, idx: number) => (
                                <React.Fragment key={idx}>
                                    <tr style={{ height: "10px" }} className="border-b border-gray-300">
                                        <td className="bg-[#F2F2F2] font-bold px-1 py-0.5 border-l border-r border-black w-20 whitespace-nowrap text-[6px]">{col.header}</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span key={i} style={{ width: "6px", minWidth: "6px" }} className="text-center font-bold">{q.correctAnswer || ''}</span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr style={{ height: "10px" }} className={`border-b border-gray-300 bg-[#FFFFCC]`}>
                                        <td className="font-bold px-1 py-0.5 border-l border-r border-black whitespace-nowrap text-[6px]">ÖĞRENCİ CEVABI</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span
                                                        key={i}
                                                        style={{ width: "6px", minWidth: "6px" }}
                                                        className={`text-center ${q.isCorrect ? 'text-green-700 font-bold' : q.isAnswered ? 'text-red-600' : ''}`}
                                                    >
                                                        {q.studentAnswer || ''}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="w-1/2">
                    <table className="w-full border border-black text-[7px] border-collapse">
                        <tbody>
                            {columns.slice(Math.ceil(columns.length / 2)).map((col: any, idx: number) => (
                                <React.Fragment key={idx}>
                                    <tr style={{ height: "10px" }} className="border-b border-gray-300">
                                        <td className="bg-[#F2F2F2] font-bold px-1 py-0.5 border-l border-r border-black w-20 whitespace-nowrap text-[6px]">{col.header}</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span key={i} style={{ width: "6px", minWidth: "6px" }} className="text-center font-bold">{q.correctAnswer || ''}</span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr style={{ height: "10px" }} className={`border-b border-gray-300 bg-[#FFFFCC]`}>
                                        <td className="font-bold px-1 py-0.5 border-l border-r border-black whitespace-nowrap text-[6px]">ÖĞRENCİ CEVABI</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span
                                                        key={i}
                                                        style={{ width: "6px", minWidth: "6px" }}
                                                        className={`text-center ${q.isCorrect ? 'text-green-700 font-bold' : q.isAnswered ? 'text-red-600' : ''}`}
                                                    >
                                                        {q.studentAnswer || ''}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex-1 flex">
                {topicCols.map((topics, colIdx) => (
                    <div key={colIdx} className={`w-1/3 flex flex-col ${colIdx < 2 ? 'border-r border-black' : ''}`}>
                        <table className="w-full text-left border-collapse text-[7px] border border-black">
                            <thead>
                                <tr className="bg-[#F8CBAD]">
                                    <th className="pl-1 text-center border-l border-b border-r border-black font-bold py-0.5">KONU ADI</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">SS</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">D</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">Y</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topics.map((t: any, idx: number) => (
                                    <React.Fragment key={idx}>
                                        {(idx === 0 || topics[idx - 1].subject !== t.subject) && (
                                            <tr className="bg-[#FFF2CC] font-bold border-t border-black">
                                                <td colSpan={5} className="pl-1 border-b border-black py-0.5">{t.subject}</td>
                                            </tr>
                                        )}
                                        <tr className="bg-white border-b border-gray-300">
                                            <td className="pl-1 border-l border-r border-gray-300 truncate max-w-[100px] py-0.5" title={t.name}>{t.name}</td>
                                            <td className="text-center border-r border-gray-300 font-bold py-0.5">{t.total}</td>
                                            <td className="text-center border-r border-gray-300 font-bold py-0.5">{t.correct}</td>
                                            <td className="text-center border-r border-gray-300 font-bold py-0.5">{t.incorrect}</td>
                                            <td className="text-center border-r font-bold py-0.5">{t.percent}</td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
};
